import React from 'react';
import {Link} from 'react-router';
import {fromJS, List} from 'immutable';
import moment from 'moment';
import {stats} from 'lib/StatsUtil';
import {pluralize} from 'lib/stringUtils';
import {renderDuration} from 'lib/timeUtils';
import {asOrderedMap} from 'lib/Immutable.utils';
import {makeLinkRowProps} from 'sg/Tables/Table/table.utils';
import WordTooltip from 'sg/WordTooltip/WordTooltip.react';
import FlatBarChart from 'generic/FlatBarChart/FlatBarChart.react';
import MarkdownRendererV2 from 'generic/MarkdownRendererV2/MarkdownRendererV2.react';
import {Button} from '@albert-io/atomic';
import {RANGES} from '@albert-io/atomic/organisms/DatePicker/DatePicker.helpers';
import classroomStore from 'client/Classrooms/Classroom/ClassroomV2.store';
import {MEGA_CLASSES_SIZE} from 'client/constants';

import classroomInsightsStore from '../ClassroomInsights.store';
import MegaClassesEmptyState from '../../MegaClassesEmptyState/MegaClassesEmptyState.react';

const csvColumns = fromJS(
  {
    Standard: {
      Standard: 'label_name',
      Description: 'label_text'
    },
    Category: 'label_category',
    Questions: 'label_count_questions',
    'Avg Time Spent (HH:MM:SS)': {
      'Avg Time Spent (seconds)': 'label_average_time_spent'
    },

    Accuracy: 'label_accuracy',
    Progress: {
      'Correct Attempts': 'label_count_questions_correct',
      'Incorrect Attempts': 'label_count_questions_incorrect',
      Unanswered: 'label_count_questions_unanswered'
    },
    'Correct Attempts': 'label_count_questions_correct',
    'Incorrect Attempts': 'label_count_questions_incorrect',
    Unanswered: 'label_count_questions_unanswered'
  },
  asOrderedMap
);

export default class StandardsPerformance extends React.Component {
  getName = (standard) => (
    <WordTooltip
      term={standard.getName()}
      content={<MarkdownRendererV2 text={standard.getText()} />}
      position='right'
      onHover
      className='classroom-insights__standard-tooltip'
    />
  );

  getCategory = (standard) => standard.getCategory();

  getQuestions = (standard) => standard.getMeta().getCountOfClassroomQuestions();

  getAverageTimeSpent = (standard) => {
    const avgTimeSpentDuration = moment.duration(
      standard.getMeta().getAvgOfClassroomGuessesTimeElapsed()
    );
    return renderDuration(avgTimeSpentDuration, {style: 'hh:mm:ss'});
  };

  getProgress = (question) => {
    const correctGuesses = this.getCorrectAttempts(question);
    const incorrectGuesses = this.getIncorrectAttempts(question);
    const totalGuesses = this.getTotalAttempts(question);
    const unansweredGuesses = this.getUnanswered(question);
    const totalPotentialGuesses = question.getMeta().getCountOfClassroomQuestionsAnswerable();

    const performanceData = fromJS([
      {
        fillColorClass: 'classroom-insights__performance--correct',
        fillPercentage: stats.percentage(correctGuesses, totalPotentialGuesses),
        keyLabel: 'Correct'
      },
      {
        fillColorClass: 'classroom-insights__performance--incorrect',
        fillPercentage: stats.percentage(incorrectGuesses, totalPotentialGuesses),
        keyLabel: 'Incorrect'
      },
      {
        fillColorClass: 'classroom-insights__performance--unanswered',
        fillPercentage: stats.percentage(unansweredGuesses, totalPotentialGuesses),
        keyLabel: 'Not Answered'
      }
    ]);

    return (
      <div className='usage-report__performance'>
        <small>
          {totalGuesses} {pluralize('total attempt', totalGuesses)}
        </small>
        <FlatBarChart inline data={performanceData} />
      </div>
    );
  };

  getAccuracy = (question) => {
    const questionMeta = question.getMeta();
    const correctGuesses = questionMeta.getCountOfClassroomCorrectGuesses();
    const incorrectGuesses = questionMeta.getCountOfClassroomIncorrectGuesses();
    const totalGuesses = correctGuesses + incorrectGuesses;
    return `${stats.percentage(correctGuesses, totalGuesses)}%`;
  };

  getTotalAttempts = (question) => {
    const correctGuesses = this.getCorrectAttempts(question);
    const incorrectGuesses = this.getIncorrectAttempts(question);
    return correctGuesses + incorrectGuesses;
  };

  getUnanswered = (question) => question.getMeta().getCountOfClassroomQuestionsUnanswered();

  getCorrectAttempts = (question) => question.getMeta().getCountOfClassroomCorrectGuesses();

  getIncorrectAttempts = (question) => question.getMeta().getCountOfClassroomIncorrectGuesses();

  getCSVHeaders = (visibleColumns) => {
    const firstColumn = csvColumns.keySeq().first();

    const visibleColumnsWithFirst = visibleColumns.toList().unshift(firstColumn);

    const formattedColumns = csvColumns
      .filter((column, key) => visibleColumnsWithFirst.includes(key))
      .flatten()
      .reduce((acc, value, key) => acc.push(`${value}=${key}`), List());

    return encodeURIComponent(formattedColumns.join(','));
  };

  getStandardLinkProps() {
    return makeLinkRowProps(
      (standard) =>
        `/classes/${classroomStore.getClassroomId()}` +
        `/insights/topic-drilldown/standard/${standard.getId()}` +
        `?attempt=${classroomInsightsStore.getAttemptNumber()}` +
        `&startDate=${classroomInsightsStore.getStartDate()}` +
        `&endDate=${classroomInsightsStore.getEndDate()}` +
        `&subjectId=${classroomInsightsStore.getSubjectId()}` +
        `&guideLevelId=${classroomInsightsStore.getGuideLevelId()}`
    );
  }

  getStandardButton = (standard) => {
    return (
      <Button
        small
        whiteBg
        onClick={(e) => e.stopPropagation()}
        as={Link}
        to={`/${standard.getSubject().getUrlSlug()}?standards=${standard.getId()}`}
      >
        Find questions
      </Button>
    );
  };

  render() {
    const classroomId = classroomStore.getClassroomId();
    const subjectId = classroomInsightsStore.getSubjectId();
    const attemptNumber = classroomInsightsStore.getAttemptNumber();
    const guideLevelId = classroomInsightsStore.getGuideLevelId();
    const assignmentId = classroomInsightsStore.getAssignmentId();
    const [startDate] = new Date(classroomInsightsStore.getStartDate()).toISOString().split('T');
    const [endDate] = new Date(classroomInsightsStore.getEndDate()).toISOString().split('T');
    const reportsLink = `${
      classroomInsightsStore.getReportType() === 'subject'
        ? `/reports/classrooms/${classroomId}/standards?attempt=${attemptNumber}&subject=${subjectId}${
            guideLevelId ? `&guideLevel=${guideLevelId}` : ''
          }&`
        : `/reports/classrooms/${classroomId}/assignments/${assignmentId}/students?`
    }range=${RANGES.CUSTOM}&start=${startDate}&end=${endDate}`;

    if (classroomStore.getActiveClassroom().getMeta().getCountOfStudents() > MEGA_CLASSES_SIZE) {
      return <MegaClassesEmptyState />;
    }

    return (
      <div className='u-display_flex u-justify-content_center u-pad_4'>
        <div>
          <h3>This report has been deprecated.</h3>
          <p>You can access the same data via the new reports feature.</p>
          <Button color='green' as='a' href={reportsLink}>
            Open Reports
          </Button>
        </div>
      </div>
    );
  }
}
